import * as React from "react"

import AudioPlayer from "@podcastsoundboard/ps-lib/components/AudioPlayer/AudioPlayer"
import ClosableDialogTitle from "@podcastsoundboard/ps-lib/components/ClosableDialogTitle"

import CheckIcon from "@mui/icons-material/Check"
import DescriptionIcon from "@mui/icons-material/Description"
import { Stack } from "@mui/material"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import CircularProgress from "@mui/material/CircularProgress"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import IconButton from "@mui/material/IconButton"
import Link from "@mui/material/Link"
import Pagination from "@mui/material/Pagination"
import Paper from "@mui/material/Paper"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import TextField from "@mui/material/TextField"
import Tooltip from "@mui/material/Tooltip"
import Typography from "@mui/material/Typography"

import searchFreesounds from "../../../../api/freesounds/search"
import LinkFreesoundAccountDialog from "../../../../components/LinkFreesoundAccountDialog"
import bitsToBytesString from "../../../../helpers/soundFile/bitsToBytesString"
import durationToMSString from "../../../../helpers/soundFile/durationToMSString"
import { useAppDispatch, useAppSelector } from "../../../../redux"
import { addSnackbar } from "../../../../redux/snackbars"
import { FreesoundClip } from "../../../../types/Freesound"

let queryTimeout: NodeJS.Timeout
const PER_PAGE = 10

function FreesoundRow({
  freesound,
  onSelected,
}: {
  freesound: FreesoundClip
  onSelected: (id: string) => void
}) {
  const [descriptionExpanded, setDescriptionExpanded] = React.useState(false)
  const darkModeEnabled = useAppSelector((state) => state.darkModeEnabled)

  const descriptionClassName =
    (darkModeEnabled ? "dark-mode-links" : "") + " sound-description"
  const fullDescriptionText = (
    <>
      <Typography
        variant="body1"
        component="div"
        color="text.secondary"
        style={{ wordBreak: "break-all" }}
      >
        <b>License:</b>{" "}
        <Link target="_blank" href={freesound.license}>
          {freesound.license}
        </Link>
      </Typography>
      <Typography
        variant="body1"
        component="div"
        color="text.secondary"
        style={{ wordBreak: "break-all" }}
      >
        <b>Webpage:</b>{" "}
        <Link target="_blank" href={freesound.url}>
          {freesound.url}
        </Link>
      </Typography>
      <Typography
        sx={{ mt: 2 }}
        className={descriptionClassName}
        color="text.secondary"
        component="div"
        variant="body1"
        dangerouslySetInnerHTML={{ __html: freesound.description }}
      />
    </>
  )

  return (
    <>
      <Dialog
        open={descriptionExpanded}
        onClose={() => setDescriptionExpanded(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle id="alert-dialog-title">{freesound.name}</DialogTitle>
        <DialogContent id="alert-dialog-title">
          {fullDescriptionText}
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => setDescriptionExpanded(false)}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
        <TableCell align="left">
          <Typography
            style={{
              overflow: "hidden",
              display: "block",
              textOverflow: "ellipsis",
            }}
            variant="body1"
          >
            {freesound.name}
          </Typography>
          <Typography
            style={{
              overflow: "hidden",
              display: "block",
              textOverflow: "ellipsis",
              fontSize: "0.75rem",
            }}
            variant="body1"
            color="text.secondary"
          >
            {bitsToBytesString(freesound.filesize)} -{" "}
            {durationToMSString(freesound.duration)}
          </Typography>

          <AudioPlayer audioUrl={freesound.previews["preview-hq-mp3"]} />
        </TableCell>
        {/* <TableCell align="left">
          <Typography color="text.secondary" variant="body1" style={{textDecoration: 'underline', cursor: 'pointer'}} onClick={() => setDescriptionExpanded(true)}>Open full description</Typography>
        </TableCell> */}
        <TableCell
          align="right"
          sx={{ pr: 2 }}
          style={{ whiteSpace: "nowrap" }}
        >
          {/* <Tooltip
            title="Preview"
            onClick={() =>
              window.open(freesound.previews["preview-hq-mp3"], "_blank")
            }
          >
            <IconButton>
              <PlayCircleOutlineIcon />
            </IconButton>
          </Tooltip> */}

          <Tooltip
            title="Description and license"
            onClick={() => setDescriptionExpanded(true)}
          >
            <IconButton>
              <DescriptionIcon />
            </IconButton>
          </Tooltip>

          <Tooltip
            title="Import sound"
            onClick={() => onSelected(freesound.id)}
          >
            <IconButton>
              <CheckIcon />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
    </>
  )
}

export default function FreesoundBrowser({
  open,
  // onSoundfileSelected,
  onClose,
  handleFreesoundSelected,
}: {
  open: boolean
  onClose: () => void
  handleFreesoundSelected: (freesoundId: string) => void
}) {
  const dispatch = useAppDispatch()
  const [searchLoading, setSearchLoading] = React.useState(false)
  const [mustLinkFreesound, setMustLinkFreesound] = React.useState(false)
  const [query, setQuery] = React.useState("")
  const [page, setPage] = React.useState(1)
  const [totalPages, setTotalPages] = React.useState(1)

  const [freesounds, setFreesounds] = React.useState<FreesoundClip[]>([])
  const darkModeEnabled = useAppSelector((state) => state.darkModeEnabled)

  const handleSearchFreesounds = React.useCallback(() => {
    if (!query) return
    searchFreesounds({
      query,
      page,
      perPage: PER_PAGE,
    })
      .then(({ freesounds, totalPages }) => {
        if (totalPages < page) setPage(totalPages)
        setFreesounds(freesounds)
        setTotalPages(totalPages)
        setSearchLoading(false)
      })
      .catch((err) => {
        console.error(err)
        setSearchLoading(false)
        const text =
          (err.response && err.response.data && err.response.data.error) ||
          err.message

        if (text === "Freesound not linked.") setMustLinkFreesound(true)

        dispatch(
          addSnackbar({
            text,
          }),
        )
      })
  }, [dispatch, page, query])

  React.useEffect(() => {
    setSearchLoading(true)
    clearTimeout(queryTimeout)
    queryTimeout = setTimeout(() => {
      handleSearchFreesounds()
    }, 300)
  }, [query, handleSearchFreesounds])

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <ClosableDialogTitle onClose={onClose}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={2}
          sx={{ py: 1, pl: 2 }}
        >
          <img
            alt=""
            role="presentation"
            src="/img/freesound-logo.png"
            style={{
              height: "45px",
              marginLeft: "-20px",
              borderRadius: "50%",
            }}
          />

          <span className={darkModeEnabled ? "dark-mode-links" : ""}>
            Import sounds from Freesound
          </span>
        </Stack>
      </ClosableDialogTitle>

      <DialogContent>
        <LinkFreesoundAccountDialog
          open={mustLinkFreesound}
          onClose={(linked) => setMustLinkFreesound(linked)}
        />

        {!mustLinkFreesound && (
          <>
            <TextField
              sx={{ mt: 1, mb: 2 }}
              size="small"
              value={query || ""}
              onChange={(e) => setQuery(e.target.value)}
              label="Search sounds"
              fullWidth
              variant="outlined"
            />

            {!query && <Typography>Please enter search query.</Typography>}
            {query && (
              <>
                {searchLoading && (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", mb: 2 }}
                  >
                    <CircularProgress />
                  </Box>
                )}

                {totalPages > 1 && (
                  <Pagination
                    sx={{ mb: 2 }}
                    count={totalPages}
                    page={page}
                    onChange={(_, v) => setPage(v)}
                  />
                )}
                {!searchLoading && freesounds.length === 0 && (
                  <Typography>No sounds found.</Typography>
                )}
                {freesounds.length > 0 && (
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell align="left">
                            <b>Sound</b>
                          </TableCell>
                          <TableCell align="right"></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {freesounds &&
                          freesounds.map((freesound) => (
                            <FreesoundRow
                              key={freesound.id}
                              freesound={freesound}
                              onSelected={handleFreesoundSelected}
                            />
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
